import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Dropdown,
  Menu,
  Select,
  Badge,
  Typography,
  Divider,
  Modal,
  Input,
  message,
  Drawer,
  Button,
  Form,
  Popover,
  Tooltip,
} from "antd";
import {
  WalletOutlined,
  SettingOutlined,
  DollarCircleOutlined,
  ShoppingCartOutlined,
  FormOutlined,
  BellOutlined,
  UserOutlined,
  MenuOutlined,
  AppstoreOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  SafetyCertificateOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { BsStars } from "react-icons/bs";
import { RxBarChart } from "react-icons/rx";
import React, {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { BusinessUniteContext, SelectedMenuOptions } from "../layout/Layout";
import {
  editUserPassword,
  editUserProfile,
  getBusinessUnitsByName,
  getNotifications,
  getOrganizations,
  getUser,
  loginSuperAdmin,
} from "../../api/Services";
import { client } from "../../api/client";

import styles from "./NavBar.module.scss";
import {
  getSelectedOrganizationName,
  getSuperAdmin,
  hasWorkingOrganization,
} from "../../api/Helper";
import NotificationItem from "../notificationitem/NotificationItem";
import GitCommit from "../../_git_commit";
import { MENU_VISIBILITY_CONFIG } from "../../config/menuVisibility";
import { useRecommendationRun } from "../../contexts/RecommendationRunContext";
import RecommendationRunBanner from "../recommendationrunbanner/RecommendationRunBanner";

type NavBarProps = {
  onChangeMenu: (value: SelectedMenuOptions) => void;
  selectedMenu: SelectedMenuOptions;
  isAdminOrg?: boolean;
  onOpenNavigation?: () => void;
};
const NavBar: FunctionComponent<NavBarProps> = ({
  onChangeMenu,
  selectedMenu,
  isAdminOrg: isAdminOrgProp,
  onOpenNavigation,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [userForm] = Form.useForm();

  const [businessUnitsOptions, setBusinessUnitsOptions] = useState<any[]>([]);
  const [organizationOptions, setOrganizationOptions] = useState<any[]>([]);
  const [hasOneBusinessUnit, setHasOneBusinessUnit] = useState(false);
  const [loadingBusinessUnit, setLoadingBusinessUnit] = useState(false);
  const [user, setUser] = useState<any>();
  const [organizationSelected, setOrganizationSelected] = useState<any>(
    hasWorkingOrganization() && localStorage.getItem("organizationId")
      ? parseInt(localStorage.getItem("organizationId")!)
      : null
  );
  const [organizationNameSelected, setOrganizationNameSelected] = useState(
    getSelectedOrganizationName()
  );
  const [isChangePasswordModalVisible, setIsChangePasswordModalVisible] =
    useState(false);
  const [isMyProfileVisible, setIsMyProfileVisible] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [actualPassword, setActualPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isLoadingChangePassword, setIsLoadingChangePassword] = useState(false);
  const [isLoadingEditProfile, setIsLoadingEditProfile] = useState(false);
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
  const [isMobileRecommendationsVisible, setIsMobileRecommendationsVisible] =
    useState(false);
  const [switchingOrganization, setSwitchingOrganization] = useState(false);
  const [isOrganizationSelectFocused, setIsOrganizationSelectFocused] =
    useState(false);
  const [apiVersion, setApiVersion] = useState<string | null>(null);
  const [dataScienceVersion, setDataScienceVersion] = useState<string | null>(
    null
  );
  const [isLoadingApiVersion, setIsLoadingApiVersion] = useState(true);
  const [isLoadingDataScienceVersion, setIsLoadingDataScienceVersion] =
    useState(false);

  const {
    contextValue: businessUnitSelected,
    updateContextValue: updateBusinessUnitSelected,
  } = useContext(BusinessUniteContext);
  const {
    currentRun,
    isActive,
    initialLoading,
    startingRun,
    cancellingRun,
    startRun,
    cancelRun,
  } = useRecommendationRun();

  useEffect(() => {
    const validateSuperAdmin = async () => {
      const isUserSuperAdmin = await getSuperAdmin();
      setIsSuperAdmin(isUserSuperAdmin);

      if (isUserSuperAdmin) {
        const organizations = await getOrganizations();
        const options = organizations
          .filter((item: any) => item.name !== "admin")
          .map((item: any) => ({
            value: item.id,
            text: item.name,
          }));
        if (organizationSelected && organizations) {
          const currentOrg = organizations.find(
            (org) => org.id == organizationSelected
          );
          setOrganizationNameSelected(currentOrg?.name ?? "");
          if (currentOrg?.name) {
            localStorage.setItem("organizationName", currentOrg.name);
          }
        }

        setOrganizationOptions(options);
      }
    };

    validateSuperAdmin();
    handleFetchNotifications();
    handleFetchUser();
  }, []);

  const handleFetchNotifications = async (page = 1) => {
    const response = await getNotifications(page);
    setUnreadNotifications(
      response.notifications.filter((not: any) => not.status === "UNREAD")
        .length
    );
    setNotifications(response.notifications);
  };

  const handleFetchUser = async () => {
    const response = await getUser();
    setApiVersion(response.apiVersion ?? null);
    setIsLoadingApiVersion(false);
    if (response.success) {
      setUser(response.user);
    }
  };

  const handleProfileMenuVisibilityChange = async (visible: boolean) => {
    if (!visible) {
      return;
    }

    setIsLoadingDataScienceVersion(true);
    try {
      const response = await client.get("/api/datascience/health");
      setDataScienceVersion(response.headers["x-api-version"] ?? null);
    } catch (error) {
      setDataScienceVersion(null);
    } finally {
      setIsLoadingDataScienceVersion(false);
    }
  };

  const handleLoginSuperAdmin = useCallback(
    async (organizationName: string) => {
      setSwitchingOrganization(true);
      try {
        const response = await loginSuperAdmin(organizationName);
        if (response.status) {
          window.location.href =
            organizationName === "admin"
              ? "/admin/overview"
              : "/commercial-analysis/summary";
          return;
        }
        message.error(t("NavBar-OrganizationSwitchError"));
      } finally {
        setSwitchingOrganization(false);
      }
    },
    [t]
  );

  const fetchBusinessUnits = async () => {
    if ((await getSuperAdmin()) && !hasWorkingOrganization()) {
      return;
    }

    setLoadingBusinessUnit(true);

    const response = await getBusinessUnitsByName("");
    const options = response.businessUnits.map((item: any) => ({
      value: item.id,
      text: item.name,
    }));
    setBusinessUnitsOptions(options);
    if (response.businessUnits[0] && !location.pathname.includes("view")) {
      updateBusinessUnitSelected(response.businessUnits[0].id);
    }
    if (response.businessUnits.length <= 1) {
      setHasOneBusinessUnit(true);
    } else {
      setHasOneBusinessUnit(false);
    }

    setLoadingBusinessUnit(false);
  };

  useEffect(() => {
    if (businessUnitSelected === null) {
      fetchBusinessUnits();
    }
  }, []);

  useEffect(() => {
    const handleRefresh = () => {
      fetchBusinessUnits();
    };

    window.addEventListener("refreshBusinessUnits", handleRefresh);
    return () =>
      window.removeEventListener("refreshBusinessUnits", handleRefresh);
  }, []);
  const handleSearchBusinessUnit = async (value: string) => {
    setLoadingBusinessUnit(true);
    const response = await getBusinessUnitsByName(value);
    const options = response.businessUnits.map((item: any) => ({
      value: item.id,
      text: item.name,
    }));
    setBusinessUnitsOptions(options);
    setLoadingBusinessUnit(false);
  };

  const handleSignOut = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("organizationId");
    localStorage.removeItem("organizationName");
    localStorage.removeItem("privileges");
    localStorage.removeItem("superAdmin");
    localStorage.removeItem("username");
    window.location.reload();
  };

  const handleChangeMenu = (key: string) => {
    if (isAdminOrg) {
      return;
    }

    onChangeMenu(key as SelectedMenuOptions);
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 8) {
      message.error(t("Common-PasswordLength"));
      return;
    }
    setIsLoadingChangePassword(true);

    const response = await editUserPassword(actualPassword, newPassword);

    if (response) {
      message.success(t("ChangePassword-SendSuccesMessage"));
      setActualPassword("");
      setNewPassword("");
      setIsChangePasswordModalVisible(false);
    } else {
      message.error(t("ChangePassword-SendErrorMessage"));
    }

    setIsLoadingChangePassword(false);
  };

  const handleEditProfile = async () => {
    await userForm.validateFields();
    setIsLoadingEditProfile(true);

    const { name, lastname } = userForm.getFieldsValue();

    const response = await editUserProfile(name, lastname);

    if (response) {
      message.success(t("Common-EditSuccessMessage"));
    } else {
      message.error(t("Common-ErrorMessage"));
    }

    setIsLoadingEditProfile(false);
  };

  const versionComponents = [
    {
      label: t("NavBar-VersionApi"),
      shortLabel: t("NavBar-VersionApi"),
      version: apiVersion,
    },
    {
      label: t("NavBar-VersionWeb"),
      shortLabel: t("NavBar-VersionWeb"),
      version: GitCommit.commitHash ?? null,
    },
    {
      label: t("NavBar-VersionDataScience"),
      shortLabel: t("NavBar-VersionDataScienceShort"),
      version: dataScienceVersion,
    },
  ];
  const versionGroups = new Map<
    string | null,
    { labels: string[]; shortLabels: string[] }
  >();

  versionComponents.forEach(({ label, shortLabel, version }) => {
    const normalizedVersion = version?.trim();
    const versionKey =
      !normalizedVersion ||
      ["unknown", "unknow version"].includes(normalizedVersion.toLowerCase())
        ? null
        : normalizedVersion;

    const group = versionGroups.get(versionKey) ?? {
      labels: [],
      shortLabels: [],
    };
    group.labels.push(label);
    group.shortLabels.push(shortLabel);
    versionGroups.set(versionKey, group);
  });

  const sharedVersion =
    versionGroups.size === 1 && !versionGroups.has(null)
      ? Array.from(versionGroups.keys())[0]
      : null;

  const versionDisplay = sharedVersion ? (
    <Typography.Text code className={styles.profileVersionCode}>
      {sharedVersion}
    </Typography.Text>
  ) : (
    <div className={styles.profileVersionLine}>
      {Array.from(versionGroups.entries()).map(([version, group], index) => (
        <React.Fragment key={version ?? "unavailable"}>
          {index > 0 && (
            <span className={styles.profileVersionSeparator}>·</span>
          )}
          <span className={styles.profileVersionItem}>
            <Tooltip title={group.labels.join(" / ")}>
              <Typography.Text className={styles.profileVersionLabel}>
                {group.shortLabels.join("/")}
              </Typography.Text>
            </Tooltip>
            {version ? (
              <Typography.Text
                code
                title={version}
                className={styles.profileVersionCode}
              >
                {version}
              </Typography.Text>
            ) : (
              <Typography.Text
                type="secondary"
                className={styles.profileVersionUnavailable}
              >
                —
              </Typography.Text>
            )}
          </span>
        </React.Fragment>
      ))}
    </div>
  );

  const profileMenu = (
    <div className={styles.profileMenuWrapper}>
      <div className={styles.profileHeader}>
        <div className={styles.profileInfo}>
          <Typography.Text strong className={styles.profileName}>
            {user?.name} {user?.lastname}
          </Typography.Text>
          <Typography.Text type="secondary" className={styles.profileEmail}>
            {user?.email}
          </Typography.Text>
        </div>
      </div>
      <Divider className={styles.profileDivider} />
      <Menu mode="inline" className={styles.profileMenu}>
        <Menu.Item
          key="profile"
          onClick={() => setIsMyProfileVisible(true)}
          className={styles.profileMenuItem}
          icon={<UserOutlined />}
        >
          {t("Common-MyProfile")}
        </Menu.Item>
        <Menu.Item
          key="changepassword"
          onClick={() => setIsChangePasswordModalVisible(true)}
          className={styles.profileMenuItem}
          icon={<SettingOutlined />}
        >
          {t("Common-ChangePassword")}
        </Menu.Item>
        <Divider className={styles.profileDivider} />
        <Menu.Item
          key="logout"
          onClick={() => handleSignOut()}
          className={styles.profileMenuLogout}
          icon={<FormOutlined />}
        >
          {t("NavBar-Logout")}
        </Menu.Item>
      </Menu>
      <div
        className={styles.profileVersionFooter}
        role="group"
        aria-label={t("NavBar-Versions")}
      >
        {isLoadingApiVersion || isLoadingDataScienceVersion ? (
          <Typography.Text
            type="secondary"
            className={styles.profileVersionLoading}
          >
            {t("NavBar-VersionsLoading")}
          </Typography.Text>
        ) : (
          versionDisplay
        )}
      </div>
    </div>
  );

  const notificationMenu = (
    <div className={styles.notificationsWrapper}>
      <Typography.Title level={4} className={styles.notificationTitle}>
        Notificaciones
      </Typography.Title>
      <Divider className={styles.notificationSeparator} />
      <div>
        {notifications.map((notification: any) => (
          <NotificationItem
            key={notification.id}
            notification={{
              id: notification.id,
              title: notification.title,
              description: notification.text,
              readed: notification.status === "READ",
              date: notification.date,
            }}
            onUpdate={handleFetchNotifications}
          />
        ))}
      </div>
    </div>
  );

  const hasWorkingOrganizationSelected =
    !isSuperAdmin ||
    (Boolean(organizationSelected) && organizationNameSelected !== "admin");
  const isAdminOrg = isAdminOrgProp ?? !hasWorkingOrganizationSelected;
  const recommendationsDisabled = isAdminOrg || initialLoading || isActive;
  const recommendationsLabel = isActive
    ? t("NavBar-RecommendationsGenerating")
    : t("NavBar-GenerateRecommendations");

  const handleStartRecommendations = () => {
    Modal.confirm({
      title: t("NavBar-GenerateRecommendations"),
      content: t("NavBar-GenerateRecommendationsConfirm"),
      okText: t("NavBar-GenerateRecommendationsConfirmOk"),
      cancelText: t("Common-Cancel"),
      onOk: startRun,
    });
  };

  const handleCancelRecommendations = () => {
    Modal.confirm({
      title: t("NavBar-CancelRecommendationsTitle"),
      content: t("NavBar-CancelRecommendationsWarning"),
      okText: t("NavBar-CancelRecommendationsConfirmOk"),
      okButtonProps: { danger: true },
      cancelText: t("Common-Cancel"),
      onOk: cancelRun,
    });
  };

  const handleViewRecommendationHistory = () => {
    setIsMobileRecommendationsVisible(false);
    navigate("/commercial-intelligence/recommendation-runs");
  };

  const renderRecommendationContent = (variant: "popover" | "mobile") =>
    currentRun ? (
      <RecommendationRunBanner
        run={currentRun}
        variant={variant}
        onCancel={handleCancelRecommendations}
        onViewHistory={handleViewRecommendationHistory}
        onGenerate={handleStartRecommendations}
        cancelling={cancellingRun}
        generating={startingRun}
      />
    ) : (
      <div
        className={
          variant === "mobile"
            ? styles.mobileRecommendationEmpty
            : styles.recommendationHistoryPopover
        }
      >
        {variant === "mobile" && (
          <Button
            type="primary"
            block
            loading={startingRun}
            disabled={recommendationsDisabled}
            onClick={handleStartRecommendations}
          >
            {t("NavBar-GenerateRecommendations")}
          </Button>
        )}
        <Button block onClick={handleViewRecommendationHistory}>
          {t("RecommendationRun-ViewHistory")}
        </Button>
      </div>
    );

  const recommendationPopoverContent = renderRecommendationContent("popover");
  const recommendationHasFailed = currentRun?.status === "FAILED";
  const mobileRecommendationIcon = recommendationHasFailed ? (
    <WarningOutlined />
  ) : isActive ? (
    <SyncOutlined spin />
  ) : (
    <ThunderboltOutlined />
  );

  const handleOpenMobileRecommendations = () => {
    setIsMobileMenuVisible(false);
    setIsMobileRecommendationsVisible(true);
  };

  const recommendationButton = (
    <Button
      type="primary"
      className={styles.recommendationsButton}
      loading={startingRun}
      disabled={recommendationsDisabled}
      onClick={handleStartRecommendations}
    >
      {recommendationsLabel}
    </Button>
  );

  return (
    <>
      <nav className={styles.navBar}>
        {isSuperAdmin && (
          <div
            className={`${styles.organizationContext} ${
              hasWorkingOrganizationSelected
                ? styles.organizationContextActive
                : styles.organizationContextAdmin
            }`}
          >
            <div className={styles.organizationModeIcon}>
              <SafetyCertificateOutlined />
            </div>
            <div className={styles.organizationSelectBlock}>
              <span className={styles.organizationContextLabel}>
                {hasWorkingOrganizationSelected
                  ? t("NavBar-ActiveOrganizationLabel")
                  : t("NavBar-AdminModeLabel")}
              </span>
              <Select
                showSearch
                placeholder={
                  isOrganizationSelectFocused
                    ? ""
                    : t("NavBar-SelectOrganization")
                }
                onFocus={() => setIsOrganizationSelectFocused(true)}
                onBlur={() => setIsOrganizationSelectFocused(false)}
                onSelect={(_, option) => {
                  if (option) {
                    const organizationName = option.label?.toString() ?? "";
                    updateBusinessUnitSelected("");
                    setOrganizationSelected(option.value);
                    setOrganizationNameSelected(organizationName);
                    handleLoginSuperAdmin(organizationName);
                  }
                }}
                onClear={() => handleLoginSuperAdmin("admin")}
                loading={switchingOrganization}
                disabled={switchingOrganization}
                className={styles.organizationSelect}
                defaultActiveFirstOption={false}
                showArrow
                dropdownMatchSelectWidth={false}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label?.toString() ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                value={organizationSelected ?? undefined}
                options={(organizationOptions || []).map((d: any) => ({
                  value: d.value,
                  label: d.text,
                }))}
              />
            </div>
            {hasWorkingOrganizationSelected && (
              <Tooltip title={t("NavBar-BackToAdmin")}>
                <Button
                  type="text"
                  shape="circle"
                  icon={<CloseCircleOutlined />}
                  loading={switchingOrganization}
                  onClick={() => handleLoginSuperAdmin("admin")}
                  className={styles.clearOrganizationButton}
                  aria-label={t("NavBar-BackToAdmin")}
                />
              </Tooltip>
            )}
          </div>
        )}
        {!hasOneBusinessUnit && !isAdminOrg && (
          <div className={styles.filterWrapper}>
            <Select
              showSearch
              defaultValue={businessUnitSelected ?? undefined}
              placeholder={t("Common-SearchBusinessUnitTextPlaceholder")}
              onSelect={(_, option) => {
                if (option) {
                  updateBusinessUnitSelected(option.value);
                }
              }}
              loading={loadingBusinessUnit}
              className={styles.filterSelect}
              defaultActiveFirstOption={false}
              showArrow={false}
              filterOption={false}
              value={businessUnitSelected ?? ""}
              onDropdownVisibleChange={() => handleSearchBusinessUnit("")}
              onSearch={handleSearchBusinessUnit}
              options={(businessUnitsOptions || []).map((d: any) => ({
                value: d.value,
                label: d.text,
              }))}
            />
          </div>
        )}
        <div className={styles.menuWrapper}>
          {/* Mobile navigation and module switcher */}
          <div className={styles.mobileMenuButton}>
            {onOpenNavigation && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={onOpenNavigation}
                className={styles.mobileMenuTrigger}
                aria-label={t("Common-Menu")}
              />
            )}
            {!isAdminOrg && (
              <Button
                type="text"
                icon={<AppstoreOutlined />}
                onClick={() => setIsMobileMenuVisible(true)}
                className={styles.mobileMenuTrigger}
                aria-label={t("Home-Modules")}
              />
            )}
          </div>

          {/* Desktop Menu */}
          <div className={styles.desktopMenu}>
            {!isAdminOrg && (
              <Menu
                mode="horizontal"
                className={styles.menu}
                onClick={(event) => handleChangeMenu(event.key)}
              >
                {MENU_VISIBILITY_CONFIG.modules.admin && (
                  <Menu.Item
                    className={`${styles.subMenu} ${
                      selectedMenu === SelectedMenuOptions.Admin &&
                      styles.selected
                    }`}
                    key="admin"
                    onClick={() => navigate("/admin/businessrules")}
                    icon={<SettingOutlined />}
                  >
                    {t("NavBar-Admin")}
                  </Menu.Item>
                )}
                <>
                  {MENU_VISIBILITY_CONFIG.modules.pricings && (
                    <Menu.Item
                      className={`${styles.subMenu} ${
                        selectedMenu === SelectedMenuOptions.Pricings &&
                        styles.selected
                      }`}
                      key="pricings"
                      onClick={() => navigate("/pricings/dashboard")}
                      icon={<DollarCircleOutlined />}
                      title={t("NavBar-Pricings")}
                    >
                      {t("NavBar-Pricings")}
                    </Menu.Item>
                  )}
                  {MENU_VISIBILITY_CONFIG.modules.promotions && (
                    <Menu.Item
                      className={`${styles.subMenu} ${
                        styles.commercialIntelligence
                      } ${
                        selectedMenu === SelectedMenuOptions.Promotions &&
                        styles.selected
                      }`}
                      key="promotions"
                      onClick={() =>
                        navigate("/commercial-analysis/summary")
                      }
                      icon={<BsStars />}
                      title={t("NavBar-CommercialIntelligence")}
                    >
                      {t("NavBar-CommercialIntelligence")}
                    </Menu.Item>
                  )}
                  {MENU_VISIBILITY_CONFIG.modules.fidelity && (
                    <Menu.Item
                      className={`${styles.subMenu} ${
                        selectedMenu === SelectedMenuOptions.Fidelity &&
                        styles.selected
                      }`}
                      key="fidelity"
                      onClick={() => navigate("/fidelity/dashboard")}
                      icon={<WalletOutlined />}
                      title={t("NavBar-Fidelity")}
                    >
                      {t("NavBar-Fidelity")}
                    </Menu.Item>
                  )}
                  {MENU_VISIBILITY_CONFIG.modules.purchaseOrders && (
                    <Menu.Item
                      className={`${styles.subMenu} ${
                        selectedMenu === SelectedMenuOptions.PurchaseOrders &&
                        styles.selected
                      }`}
                      key="purchaseorders"
                      onClick={() => navigate("/purchaseorders/dashboard")}
                      icon={<ShoppingCartOutlined />}
                      title={t("NavBar-Shopping")}
                    >
                      {t("NavBar-Shopping")}
                    </Menu.Item>
                  )}
                  {MENU_VISIBILITY_CONFIG.modules.repositionOrders && (
                    <Menu.Item
                      className={`${styles.subMenu} ${
                        selectedMenu === SelectedMenuOptions.RepositionOrders &&
                        styles.selected
                      }`}
                      key="repositionorders"
                      onClick={() => navigate("/repositionorders/dashboard")}
                      icon={<FormOutlined />}
                      title={t("NavBar-Surtido")}
                    >
                      {t("NavBar-Surtido")}
                    </Menu.Item>
                  )}
                  {MENU_VISIBILITY_CONFIG.modules.commercialAnalysis && (
                    <Menu.Item
                      className={`${styles.subMenu} ${
                        selectedMenu ===
                          SelectedMenuOptions.CommercialAnalysis &&
                        styles.selected
                      }`}
                      key="commercialanalysis"
                      onClick={() => navigate("/commercial-analysis")}
                      icon={<RxBarChart />}
                      title={t("NavBar-CommercialAnalysis")}
                    >
                      {t("NavBar-CommercialAnalysis")}
                    </Menu.Item>
                  )}
                </>
              </Menu>
            )}
          </div>

          <div className={styles.menuSpacer} />

          <div className={styles.iconsWrapper}>
            {!isAdminOrg && (
              <Popover
                content={recommendationPopoverContent}
                trigger="hover"
                placement="bottom"
              >
                <span className={styles.recommendationsButtonWrapper}>
                  {recommendationButton}
                </span>
              </Popover>
            )}
            <Dropdown
              overlay={profileMenu}
              placement="bottomRight"
              onVisibleChange={handleProfileMenuVisibilityChange}
            >
              <UserOutlined className={styles.notificationIcon} />
            </Dropdown>
            <Dropdown overlay={notificationMenu} placement="bottomRight">
              <Badge count={unreadNotifications}>
                <BellOutlined className={styles.notificationIcon} />
              </Badge>
            </Dropdown>
          </div>
        </div>
      </nav>
      <Drawer
        visible={isMyProfileVisible}
        onClose={() => setIsMyProfileVisible(false)}
      >
        <div>
          <Typography.Title level={3}>{t("Common-MyProfile")}</Typography.Title>
          <Divider />
          <div className={styles.profileWrapper}>
            <Form
              name="userForm"
              initialValues={{ remember: true }}
              autoComplete="off"
              form={userForm}
              layout="vertical"
            >
              <Form.Item
                label={t("Common-Name")}
                name="name"
                initialValue={user?.name}
                rules={[
                  {
                    required: true,
                    message: t("Common-NameNotEntered"),
                  },
                ]}
              >
                <Input placeholder={t("Common-Name")} />
              </Form.Item>
              <Form.Item
                label={t("Common-LastName")}
                name="lastname"
                initialValue={user?.lastname}
                rules={[
                  {
                    required: true,
                    message: t("Common-LastName"),
                  },
                ]}
              >
                <Input placeholder={t("Common-LastName")} />
              </Form.Item>
              <Form.Item
                className={styles.contactItem}
                label={t("Common-Email")}
                name="email"
                rules={[
                  {
                    type: "email",
                  },
                ]}
                initialValue={user?.email}
              >
                <Input disabled />
              </Form.Item>
              <div className={styles.profileButtons}>
                <Form.Item>
                  <Button
                    onClick={() => {
                      userForm.resetFields();
                    }}
                    className={styles.cancelButton}
                    type="primary"
                  >
                    {t("Common-Cancel")}
                  </Button>
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    onClick={handleEditProfile}
                    loading={isLoadingEditProfile}
                  >
                    {t("Common-Save")}
                  </Button>
                </Form.Item>
              </div>
            </Form>
          </div>
        </div>
      </Drawer>

      {/* Mobile Menu Drawer */}
      <Drawer
        title={t("Home-Modules")}
        placement="bottom"
        visible={isMobileMenuVisible}
        onClose={() => setIsMobileMenuVisible(false)}
        className={styles.mobileMenuDrawer}
        height="auto"
        destroyOnClose
      >
        {!isAdminOrg && (
          <Button
            type="primary"
            block
            className={styles.mobileRecommendationsButton}
            icon={mobileRecommendationIcon}
            onClick={handleOpenMobileRecommendations}
          >
            {t("NavBar-Recommendations")}
          </Button>
        )}
        <Menu
          mode="vertical"
          className={styles.mobileMenu}
          onClick={(event) => {
            handleChangeMenu(event.key);
            setIsMobileMenuVisible(false);
          }}
        >
          {!isAdminOrg && (
            <>
              {MENU_VISIBILITY_CONFIG.modules.admin && (
                <Menu.Item
                  className={`${styles.mobileMenuItem} ${
                    selectedMenu === SelectedMenuOptions.Admin &&
                    styles.selected
                  }`}
                  key="admin"
                  onClick={() => navigate("/admin/businessrules")}
                  icon={<SettingOutlined />}
                >
                  {t("NavBar-Admin")}
                </Menu.Item>
              )}
              {MENU_VISIBILITY_CONFIG.modules.pricings && (
                <Menu.Item
                  className={`${styles.mobileMenuItem} ${
                    selectedMenu === SelectedMenuOptions.Pricings &&
                    styles.selected
                  }`}
                  key="pricings"
                  onClick={() => navigate("/pricings/dashboard")}
                  icon={<DollarCircleOutlined />}
                >
                  {t("NavBar-Pricings")}
                </Menu.Item>
              )}
              {MENU_VISIBILITY_CONFIG.modules.promotions && (
                <Menu.Item
                  className={`${styles.mobileMenuItem} ${
                    styles.commercialIntelligence
                  } ${
                    selectedMenu === SelectedMenuOptions.Promotions &&
                    styles.selected
                  }`}
                  key="promotions"
                  onClick={() => navigate("/commercial-analysis/summary")}
                  icon={<BsStars />}
                >
                  {t("NavBar-CommercialIntelligence")}
                </Menu.Item>
              )}
              {MENU_VISIBILITY_CONFIG.modules.fidelity && (
                <Menu.Item
                  className={`${styles.mobileMenuItem} ${
                    selectedMenu === SelectedMenuOptions.Fidelity &&
                    styles.selected
                  }`}
                  key="fidelity"
                  onClick={() => navigate("/fidelity/dashboard")}
                  icon={<WalletOutlined />}
                >
                  {t("NavBar-Fidelity")}
                </Menu.Item>
              )}
              {MENU_VISIBILITY_CONFIG.modules.purchaseOrders && (
                <Menu.Item
                  className={`${styles.mobileMenuItem} ${
                    selectedMenu === SelectedMenuOptions.PurchaseOrders &&
                    styles.selected
                  }`}
                  key="purchaseorders"
                  onClick={() => navigate("/purchaseorders/dashboard")}
                  icon={<ShoppingCartOutlined />}
                >
                  {t("NavBar-Shopping")}
                </Menu.Item>
              )}
              {MENU_VISIBILITY_CONFIG.modules.repositionOrders && (
                <Menu.Item
                  className={`${styles.mobileMenuItem} ${
                    selectedMenu === SelectedMenuOptions.RepositionOrders &&
                    styles.selected
                  }`}
                  key="repositionorders"
                  onClick={() => navigate("/repositionorders/dashboard")}
                  icon={<FormOutlined />}
                >
                  {t("NavBar-Surtido")}
                </Menu.Item>
              )}
              {MENU_VISIBILITY_CONFIG.modules.commercialAnalysis && (
                <Menu.Item
                  className={`${styles.mobileMenuItem} ${
                    selectedMenu === SelectedMenuOptions.CommercialAnalysis &&
                    styles.selected
                  }`}
                  key="commercialanalysis"
                  onClick={() => navigate("/commercial-analysis")}
                  icon={<RxBarChart />}
                >
                  {t("NavBar-CommercialAnalysis")}
                </Menu.Item>
              )}
            </>
          )}
        </Menu>
      </Drawer>

      <Drawer
        title={t("NavBar-Recommendations")}
        placement="bottom"
        visible={isMobileRecommendationsVisible}
        onClose={() => setIsMobileRecommendationsVisible(false)}
        className={`${styles.mobileMenuDrawer} ${styles.mobileRecommendationsDrawer}`}
        height="auto"
        destroyOnClose
      >
        {renderRecommendationContent("mobile")}
      </Drawer>

      <Modal
        title={t("Common-ChangePassword")}
        visible={isChangePasswordModalVisible}
        okText={t("Common-Save")}
        cancelText={t("Common-Cancel")}
        okButtonProps={{
          disabled: !actualPassword || !newPassword,
          loading: isLoadingChangePassword,
          className: styles.modalButton,
        }}
        cancelButtonProps={{
          className: styles.modalButton,
        }}
        onCancel={() => {
          setIsChangePasswordModalVisible(false);
          setActualPassword("");
          setNewPassword("");
        }}
        onOk={handleChangePassword}
        className={styles.changePasswordModal}
        width={400}
      >
        <div className={styles.passwordForm}>
          <Form layout="vertical">
            <Form.Item label={t("Common-ActualPassword")} required>
              <Input.Password
                className={styles.passwordInput}
                placeholder={t("Common-Password")}
                value={actualPassword}
                onChange={(e) => setActualPassword(e.target.value)}
                minLength={8}
                required={true}
              />
            </Form.Item>
            <Form.Item label={t("Common-NewPassword")} required>
              <Input.Password
                className={styles.passwordInput}
                placeholder={t("Common-Password")}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={8}
                required={true}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default NavBar;
